import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Loader2, AlertTriangle } from "lucide-react";

function Metric({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="font-display text-3xl font-bold text-slate-900">{value}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Progress() {
  const { data, isLoading } = useQuery({
    queryKey: ["progress"],
    queryFn: async () => (await api.get("/student/progress")).data,
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-slate-400">Progress</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Your progress</h1>
      </div>

      <div>
        <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">This week</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Metric label="Reviews" value={data.reviews_week} />
          <Metric label="Successful recall" value={`${data.recall}%`} />
          <Metric label="Study days" value={data.study_days} sub="of 7" />
          <Metric label="Reviewed today" value={data.reviews_today} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Metric label="Cards learned" value={data.cards_learned} />
        <Metric label="Cards due now" value={data.cards_due} />
      </div>

      <div>
        <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">Needs attention</h2>
        {data.weak_topics.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500" data-testid="no-weak-topics">
            Keep studying and we'll highlight any topics that need more work here.
          </div>
        ) : (
          <div className="space-y-2" data-testid="weak-topics">
            {data.weak_topics.map((w) => (
              <div key={w.topic} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-5 py-3">
                <span className="flex items-center gap-2 text-slate-800">
                  <AlertTriangle className="h-4 w-4 text-amber-500" /> {w.topic}
                </span>
                <span className="font-mono text-sm font-medium text-amber-600">{w.recall}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
